import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

/* ── static underwater curve data ── */

const underwaterData = [
  { date: 'Jul 21', value: 0 },
  { date: '', value: -3 },
  { date: '', value: -8 },
  { date: '', value: -5 },
  { date: '', value: -2 },
  { date: '10ct 26', value: 0 },
  { date: '', value: -6 },
  { date: '', value: -12 },
  { date: '', value: -9 },
  { date: '18 Oct 29', value: -4 },
  { date: '', value: -1 },
  { date: '', value: 0 },
  { date: '', value: -2 },
  { date: '', value: -7 },
  { date: '19 Oct 21', value: -14 },
  { date: '', value: -18 },
  { date: '', value: -22 },
  { date: '', value: -26.4 },
  { date: '', value: -24 },
  { date: '', value: -20 },
  { date: '20ct 6', value: -16 },
  { date: '', value: -13 },
  { date: '', value: -9 },
  { date: '', value: -5 },
  { date: '', value: -2 },
  { date: '2Sep 14', value: 0 },
  { date: '', value: -4 },
  { date: '', value: -10 },
  { date: '', value: -16 },
  { date: '22 Mar 19', value: -20 },
  { date: '', value: -17 },
  { date: '', value: -13 },
  { date: '', value: -8 },
  { date: '', value: -4 },
  { date: '23 May 23', value: -1 },
  { date: '', value: 0 },
  { date: '', value: -3 },
  { date: '', value: -8 },
  { date: '', value: -12 },
  { date: '24 Dec 27', value: -15 },
  { date: '', value: -11 },
  { date: '', value: -7 },
  { date: '', value: -3 },
  { date: 'Mar 19, 2', value: -6.6 },
];

/* ── component ── */

export default function DrawdownAnalysisMock() {
  return (
    <div
      style={{
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        borderRadius: 12,
        overflow: 'hidden',
        fontFamily:
          'Inter, ui-sans-serif, system-ui, -apple-system, sans-serif',
      }}
    >
      {/* header */}
      <div style={{ background: '#111827', padding: '18px 24px 0' }}>
        <h3
          style={{
            color: '#f9fafb',
            fontSize: 14,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            margin: 0,
          }}
        >
          Drawdown Analysis
        </h3>
        <p style={{ color: '#9ca3af', fontSize: 12, margin: '6px 0 16px' }}>
          Risk profile: depth, duration, and resolution characteristics
        </p>
      </div>

      {/* 2x2 grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
          padding: '12px 24px 24px',
          background: '#111827',
        }}
      >
        {/* top-left: Risk Profile */}
        <Card>
          <SectionLabel>Risk Profile</SectionLabel>
          <p
            style={{
              color: '#9ca3af',
              fontSize: 10,
              margin: '4px 0 2px',
            }}
          >
            Maximum Drawdown
          </p>
          <p
            style={{
              color: '#f43f5e',
              fontSize: 28,
              fontWeight: 700,
              margin: '0 0 4px',
              fontFamily: 'monospace',
            }}
          >
            26.4%
          </p>
          <p
            style={{
              color: '#9ca3af',
              fontSize: 10,
              margin: '0 0 14px',
            }}
          >
            Currently 6.6% below peak
          </p>

          <div
            style={{
              borderTop: '1px solid #374151',
              paddingTop: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <MetricRow label="Drawdowns observed" value="9" />
            <MetricRow label="Recovered" value="8 / 9" />
          </div>
        </Card>

        {/* top-right: Duration & Recovery */}
        <Card>
          <SectionLabel>Duration &amp; Recovery</SectionLabel>

          <div style={{ marginTop: 8 }}>
            <SubLabel>Pain Length</SubLabel>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                marginBottom: 12,
              }}
            >
              <MetricRow label="Longest" value="1268d" />
              <MetricRow label="Average" value="258.25d" />
            </div>
          </div>

          <div style={{ borderTop: '1px solid #374151', paddingTop: 10 }}>
            <SubLabel>Recovery Tail</SubLabel>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              <MetricRow label="Median recovery" value="18d" />
              <MetricRow label="Longest stagnation" value="1323d" />
            </div>
          </div>
        </Card>

        {/* bottom-left: Underwater Curve */}
        <Card>
          <SectionLabel>Underwater Curve</SectionLabel>
          <div style={{ width: '100%', height: 170, marginTop: 8 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={underwaterData}
                margin={{ top: 5, right: 5, left: -15, bottom: 0 }}
              >
                <defs>
                  <linearGradient
                    id="ddGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor="#e11d48"
                      stopOpacity={0.18}
                    />
                    <stop
                      offset="100%"
                      stopColor="#e11d48"
                      stopOpacity={0.01}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#374151"
                  opacity={0.3}
                />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 8, fill: '#9CA3AF' }}
                  axisLine={{ stroke: '#4B5563' }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fontSize: 9, fill: '#9CA3AF' }}
                  axisLine={{ stroke: '#4B5563' }}
                  domain={['dataMin', 0]}
                  reversed
                />
                <ReferenceLine
                  y={0}
                  stroke="#d1d5db"
                  strokeDasharray="3 3"
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#e11d48"
                  strokeWidth={1.5}
                  fill="url(#ddGradient)"
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: 6,
                    fontSize: 11,
                    color: '#F9FAFB',
                  }}
                  formatter={(value: number) => [
                    `${value.toFixed(1)}%`,
                    'Drawdown',
                  ]}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* bottom-right: Observed Pattern */}
        <Card>
          <SectionLabel>Observed Pattern</SectionLabel>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              marginTop: 8,
            }}
          >
            <PatternRow label="Frequency" value="Periodic, moderate" />
            <PatternRow
              label="Recovery"
              value="Recovery tails dominate risk, uneven pacing"
            />
            <PatternRow
              label="Spacing"
              value="Well-distributed (43-164d)"
            />
            <PatternRow
              label="Resolution"
              value="Mostly recovered, patience required"
            />
          </div>

          <p
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#d1d5db',
              marginTop: 16,
              lineHeight: 1.5,
              borderTop: '1px solid #374151',
              paddingTop: 12,
            }}
          >
            Moderate drawdowns with prolonged recovery tails.
          </p>
        </Card>
      </div>

      {/* expandable footer hint */}
      <div
        style={{
          background: '#111827',
          padding: '0 24px 18px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            color: '#6b7280',
            cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: 10 }}>&#9662;</span>
          Individual Drawdown Periods (9)
        </div>
      </div>
    </div>
  );
}

/* ── helper components ── */

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: '#1f2937',
        borderRadius: 8,
        padding: 14,
        border: '1px solid #374151',
      }}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: '#9ca3af',
        margin: 0,
      }}
    >
      {children}
    </p>
  );
}

function SubLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      style={{
        fontSize: 9,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: '#6b7280',
        margin: '0 0 6px',
      }}
    >
      {children}
    </p>
  );
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: 11,
      }}
    >
      <span style={{ color: '#9ca3af' }}>{label}</span>
      <span style={{ color: '#f9fafb', fontWeight: 600 }}>{value}</span>
    </div>
  );
}

function PatternRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ fontSize: 11 }}>
      <span style={{ color: '#9ca3af' }}>{label}: </span>
      <span style={{ color: '#f9fafb', fontWeight: 600 }}>{value}</span>
    </div>
  );
}
